import { devLog } from '../../utils/devLog';

/**
 * Dev-only warning emitted when icon/logo content props conflict with each
 * other or with text-button props, or when an icon-/logo-only button is
 * missing an accessible name (`aria-label` or `aria-labelledby`).
 *
 * Rules:
 * - `icon` (icon-only) and `logo` (logo-only) are mutually exclusive with each
 *   other and with every other content prop (`children`, `startIcon`,
 *   `endIcon`, `startLogo`, `endLogo`).
 * - `startIcon` and `startLogo` occupy the same slot; only one may be used.
 * - `endIcon` and `endLogo` occupy the same slot; only one may be used.
 * - Icon-/logo-only buttons must have `aria-label` or `aria-labelledby`.
 */
export function warnButtonContentConflicts(icon, children, startIcon, endIcon, ariaLabel, ariaLabelledBy, logo, startLogo, endLogo) {
  const hasIcon = icon != null;
  const hasLogo = logo != null;

  // Accessibility check runs first and unconditionally: a missing accessible name
  // is independent of any prop-combination mistake and must always be surfaced.
  if ((hasIcon || hasLogo) && (ariaLabel == null || ariaLabel === '') && (ariaLabelledBy == null || ariaLabelledBy === '')) {
    devLog('Button: icon-only and logo-only buttons must have an `aria-label` or `aria-labelledby` ' + 'so screen readers can announce the action.');
  }

  // When `icon` and `logo` are both set, surface that root conflict and stop —
  // any sibling warnings would just be noise downstream of the same mistake.
  if (hasIcon && hasLogo) {
    devLog('Button: `icon` and `logo` are mutually exclusive. ' + 'Use `icon` for monochrome icons or `logo` for multicolor artwork — not both.');
    return;
  }
  if (hasIcon) {
    warnOnlyModeConflicts('icon', {
      children,
      startIcon,
      endIcon,
      startLogo,
      endLogo
    });
  }
  if (hasLogo) {
    warnOnlyModeConflicts('logo', {
      children,
      startIcon,
      endIcon,
      startLogo,
      endLogo
    });
  }
  if (startIcon != null && startLogo != null) {
    devLog('Button: `startIcon` and `startLogo` occupy the same slot. ' + 'Use one or the other — not both.');
  }
  if (endIcon != null && endLogo != null) {
    devLog('Button: `endIcon` and `endLogo` occupy the same slot. ' + 'Use one or the other — not both.');
  }
}
const ONLY_MODE_SIBLINGS = ['children', 'startIcon', 'endIcon', 'startLogo', 'endLogo'];
function warnOnlyModeConflicts(prop, siblings) {
  ONLY_MODE_SIBLINGS.forEach(sibling => {
    const value = siblings[sibling];
    const isSet = sibling === 'children' ? value != null && value !== '' : value != null;
    if (!isSet) return;
    devLog(`Button: \`${prop}\` and \`${sibling}\` are mutually exclusive. ` + `Use \`${prop}\` for ${prop}-only buttons — not alongside \`${sibling}\`.`);
  });
}